import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { ArrowLeft, Lock, CheckCircle2, ChevronRight, PlayCircle, Award } from "lucide-react";
import { BottomNav } from "@/components/student/BottomNav";

export default function CoursesPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/courses`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => {
      setCourses(r.data.courses || []);
      if (r.data.courses?.[0]) setOpen(r.data.courses[0].id);
    }).catch((e) => console.error("courses load failed", e?.response?.status));
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-white border-b px-4 py-3">
        <button onClick={() => navigate("/student")} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-semibold" data-testid="courses-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="courses-page">
        <h1 className="text-3xl font-extrabold text-slate-800 font-['Fredoka'] mb-1">My Courses</h1>
        <p className="text-slate-500 mb-6 font-['Lexend']">Learn step by step — master a lesson to unlock the next.</p>

        <div className="space-y-4">
          {courses.map((c) => (
            <div key={c.id} className="rounded-3xl bg-white border-2 border-slate-200 overflow-hidden" data-testid={`course-${c.id}`}>
              <button onClick={() => setOpen(open === c.id ? null : c.id)} className="w-full flex items-center gap-4 p-4 text-left">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: c.color + "22" }}>
                  <span className="text-lg font-extrabold font-['Fredoka']" style={{ color: c.color }}>{c.done}/{c.total}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-800 truncate">{c.name}</p>
                    {c.completed && <Award className="w-4 h-4 text-amber-500" />}
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${c.percent}%`, background: c.color }} />
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${open === c.id ? "rotate-90" : ""}`} />
              </button>

              {open === c.id && (
                <div className="px-4 pb-4 space-y-4">
                  {c.levels.map((lv, li) => (
                    <div key={li}>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{lv.name}</p>
                      <div className="space-y-2">
                        {lv.lessons.map((ls) => (
                          <button
                            key={ls.id}
                            disabled={ls.locked}
                            onClick={() => navigate(`/courses/${c.id}/${ls.id}`)}
                            data-testid={`lesson-${ls.id}`}
                            className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-all ${ls.locked ? "bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed" : "bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm"}`}
                          >
                            {ls.completed ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                              : ls.locked ? <Lock className="w-5 h-5 text-slate-300 shrink-0" />
                              : <PlayCircle className="w-5 h-5 text-indigo-500 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold truncate ${ls.locked ? "text-slate-400" : "text-slate-800"}`}>{ls.title}</p>
                              {ls.completed && <p className="text-[11px] text-green-600">Mastered · {ls.mastery}%</p>}
                            </div>
                            {!ls.locked && <ChevronRight className="w-4 h-4 text-slate-300" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
